import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BsToast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';

/**
 * Toast — feedback global no canto inferior direito.
 *
 * Base Bootstrap: <ToastContainer> + <Toast> (react-bootstrap). Posição, stack,
 * autohide e aria-live vêm do primitivo; a gente veste com --bs-toast-* nos
 * tokens (redline §3).
 *
 * <p>Um ToastProvider único no root da árvore expõe `useToast()`. Cada
 * `showToast({...})` empilha (mais novo embaixo, gap 10px, auto-dismiss 3s).
 * O pill é ink-900 em TODAS as variantes — a cor vive no disco do ícone
 * (redline §3).
 *
 * <p>Uso — `message`/`actionLabel` recebem TEXTO já resolvido (via `t()`) ou
 * um ReactNode (`<Trans i18nKey="..."/>`). O primitivo NÃO chama `t()` nem
 * envolve em `<Trans>` — antes envolvia strings em `<Trans i18nKey={message}>`
 * o que gerava `missingKey` no i18next quando o caller já tinha passado texto
 * traduzido (que é o caminho comum via shim MainPage.showToast).
 *
 * ```jsx
 * const toast = useToast();
 * toast.showToast({ variant: 'success', message: t('schools.save.success') });
 * toast.showToast({
 *   variant: 'error',
 *   message: t('error.api.general'),
 *   actionLabel: t('app.feedback.retry'),
 *   onAction: () => retryFn(),
 * });
 * // ou com Trans:
 * toast.showToast({ message: <Trans i18nKey="schools.save.success"/> });
 * ```
 *
 * <p>Fora do escopo aqui: erro de campo (vira `is-invalid` no FormField, nunca
 * Toast) e estado persistente (vira PageBanner).
 */

const ToastContext = createContext(null);

let toastIdSeq = 0;

/* ─────────── Glyphs Lucide inline ───────────
 * TODOS com stroke-linecap="round" — sem isso o ponto do "!" em M12 17h.01
 * não renderiza e o disco aparece quase vazio (bug pego no próprio hifi).
 */
const GlyphCheck = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} aria-hidden="true">
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

const GlyphX = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} aria-hidden="true">
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

const GlyphBang = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} aria-hidden="true">
		<path d="M12 8v5" />
		<path d="M12 17h.01" />
	</svg>
);

const GlyphInfo = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} aria-hidden="true">
		<path d="M12 11v5" />
		<path d="M12 7h.01" />
	</svg>
);

const GlyphClose = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} aria-hidden="true">
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

// 'warn' e 'warning' são aliases pra mesma variante (âmbar). PageBanner e
// ConfirmDialog usam 'warn'; o shim antigo do MainPage passa 'warning'. Ambos
// funcionam pra evitar fall-through silencioso pro info default.
const VARIANTS = {
	success: { discClass: 'rt-disc-ok', Glyph: GlyphCheck },
	error:   { discClass: 'rt-disc-err', Glyph: GlyphX },
	warning: { discClass: 'rt-disc-warn', Glyph: GlyphBang },
	warn:    { discClass: 'rt-disc-warn', Glyph: GlyphBang },
	info:    { discClass: 'rt-disc-info', Glyph: GlyphInfo },
};

/* ─────────── Dedup window ───────────
 * Emissões idênticas (mesma variante + mesma mensagem) dentro de 500ms são
 * ignoradas. Protege contra:
 *  · React 18 StrictMode em dev (roda useEffect 2× de propósito) — cenário
 *    canônico: toast disparado num effect que lê `auth.globalSuccess` roda
 *    duas vezes no mount.
 *  · Double-click em prod (user impaciente).
 *  · Re-render em cadeia (parent + child ambos disparam).
 * Callers com semântica de "quero mesmo N iguais" podem passar `dedupKey`
 * único (ex.: id do item de lote) pra bypassar. */
const DEDUP_WINDOW_MS = 500;

export function ToastProvider({ children }) {
	const [items, setItems] = useState([]);
	// { [dedupKey]: timestampMs }
	const lastEmitRef = useRef(new Map());

	const remove = useCallback((id) => {
		setItems((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const showToast = useCallback((opts) => {
		const variant = opts.variant || 'success';
		const msgKey = typeof opts.message === 'string' ? opts.message : '';
		const key = opts.dedupKey || `${variant}|${msgKey}`;
		const now = Date.now();
		const last = lastEmitRef.current.get(key);
		if (last && now - last < DEDUP_WINDOW_MS) return;
		lastEmitRef.current.set(key, now);

		const id = ++toastIdSeq;
		setItems((prev) => [
			...prev,
			{
				id,
				variant,
				message: opts.message,
				actionLabel: opts.actionLabel,
				onAction: opts.onAction,
				durationMs: opts.durationMs != null ? opts.durationMs : 3000,
			},
		]);
	}, []);

	const dismissToast = useCallback(() => setItems([]), []);

	// Helper dev-only: expõe `showToast` no console pra validar variantes,
	// empilhamento e dedup sem depender de achar um fluxo real que dispare
	// em sequência. Guardado por NODE_ENV — não vaza pra bundle de prod.
	// Cleanup no unmount evita ref morta em hot-reload do CRA.
	useEffect(() => {
		if (process.env.NODE_ENV !== 'development') return;
		window.__toast = showToast;
		return () => { delete window.__toast; };
	}, [showToast]);

	return (
		<ToastContext.Provider value={{ showToast, dismissToast }}>
			{children}
			<ToastContainer className="rt-tc" containerPosition="fixed">
				{items.map((item) => {
					const v = VARIANTS[item.variant] || VARIANTS.info;
					const Glyph = v.Glyph;
					return (
						<ToastEntry
							key={item.id}
							item={item}
							discClass={v.discClass}
							Glyph={Glyph}
							onRemove={() => remove(item.id)}
						/>
					);
				})}
			</ToastContainer>
		</ToastContext.Provider>
	);
}

function ToastEntry({ item, discClass, Glyph, onRemove }) {
	const { t } = useTranslation();
	const closeAria = t('app.feedback.close', { defaultValue: 'Fechar' });
	return (
		<BsToast
			className="rt"
			onClose={onRemove}
			autohide
			delay={item.durationMs}
			role="status"
			aria-live="polite"
			aria-atomic="true"
		>
			<span className={`rt-disc ${discClass}`}><Glyph /></span>
			<span className="rt-msg">{item.message}</span>
			{item.actionLabel && (
				<button
					type="button"
					className="rt-action"
					onClick={() => {
						if (item.onAction) item.onAction();
						onRemove();
					}}
				>
					{item.actionLabel}
				</button>
			)}
			<button
				type="button"
				className="rt-close"
				onClick={onRemove}
				aria-label={closeAria}
			>
				<GlyphClose />
			</button>
		</BsToast>
	);
}

export function useToast() {
	const ctx = useContext(ToastContext);
	if (!ctx) {
		throw new Error('useToast must be used inside a <ToastProvider>');
	}
	return ctx;
}
